import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeNext } from '../AuthCallback/AuthCallbackPage';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { SsoProvider, Route } from '../../../enums';
import { common } from '../../../i18n/common.en';
import { useAuthStore } from '../../../store/auth.store';
import type { ProvidersInfo } from '../../../types';
import { ApiClientError } from '../../../services/api';
import { loginService } from './login.service';
import { Wordmark } from '../../shared/Brand';
import { brand } from '../../../brand';
import { i18n } from './i18n.en';

const ICON: Record<string, string> = { google: 'pi pi-google', microsoft: 'pi pi-microsoft', oidc: 'pi pi-shield' };

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Where to go after sign-in: the page the person was heading to (an agent's authorize page, a deep link), else Apps.
  const next = safeNext(params.get('redirect'));
  const token = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const [info, setInfo] = useState<ProvidersInfo | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState<{ clientName: string; connectorName: string } | null>(null);
  // Opened by an agent's authorization (?redirect=/authorize?req=…): say who is waiting, before any sign-in.
  const pendingReq = useMemo(() => {
    const m = /^\/authorize\?(?:.*&)?req=([^&]+)/.exec(next);
    return m ? decodeURIComponent(m[1]!) : null;
  }, [next]);
  useEffect(() => {
    if (!pendingReq) return;
    loginService.authorizePreview(pendingReq).then(setWaiting).catch(() => undefined);
  }, [pendingReq]);

  useEffect(() => {
    if (token) navigate(next, { replace: true });
  }, [token, navigate, next]);

  useEffect(() => {
    loginService
      .providers()
      .then(setInfo)
      .catch((e: ApiClientError) => setError(e.message));
  }, []);

  const submitLocal = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await loginService.localLogin(email);
      setSession(r.accessToken, r.principal);
      navigate(next, { replace: true });
    } catch (err) {
      setError((err as ApiClientError).message);
    } finally {
      setBusy(false);
    }
  };

  const providers = info ? (info.providers?.length ? info.providers : [info.provider]) : [];
  const sso = providers.filter((p) => p !== SsoProvider.Local);
  const local = providers.includes(SsoProvider.Local) && info?.localLoginEnabled !== false;
  const redirect = next === Route.Apps ? undefined : next;
  const peer = brand.id === 'mcpeer';

  return (
    <div className="mcper-login">
      <Card className="mcper-login-card">
        <div className="flex flex-column gap-4">
          <a href={brand.homeUrl} style={{ display: 'inline-block', alignSelf: 'flex-start' }}>
            <Wordmark tone="light" title={common.app.name} />
          </a>
          <div className="mcper-stack">
            <h1 className="mcper-h1" style={{ fontSize: 26 }}>
              {peer ? i18n.peer.title : i18n.title}
            </h1>
            <span className="mcper-muted">{peer ? i18n.peer.subtitle : info ? (info.openRegistration ? i18n.subtitlePublic : i18n.subtitle(info.orgName)) : i18n.loadingProviders}</span>
          </div>

          {error && <Message severity="error" text={error} />}

          {sso.map((p) => (
            <Button
              key={p}
              label={i18n.continueWith((info?.openRegistration ? i18n.providerNamePublic[p] : undefined) ?? i18n.providerName[p] ?? p)}
              icon={ICON[p] ?? 'pi pi-sign-in'}
              outlined={p !== sso[0]}
              onClick={() => loginService.startSso(redirect, providers.length > 1 ? p : undefined)}
            />
          ))}

          {local && (
            <form onSubmit={submitLocal} className="flex flex-column gap-3">
              <label htmlFor="email" className="font-semibold" style={{ fontSize: 13.5 }}>
                {i18n.emailLabel}
              </label>
              <InputText id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={i18n.emailPlaceholder} required autoFocus />
              <Button type="submit" label={i18n.localButton} loading={busy} outlined={sso.length > 0} />
              <small className="mcper-muted">{i18n.localHint}</small>
            </form>
          )}

          {waiting && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, background: peer ? '#0B1020' : 'var(--mcper-neutral-bg)', border: `1px solid ${peer ? '#1E2A44' : 'var(--mcper-line)'}` }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--mcper-accent-bright)', flex: 'none' }} />
              <span style={{ fontSize: 13.5, flex: '1 1 0', minWidth: 0, color: peer ? '#B7C2D3' : 'var(--mcper-neutral)' }}>
                {(peer ? i18n.waitingPeer : i18n.waiting)(waiting.clientName, waiting.connectorName).map((part, idx) => (idx % 2 === 0 ? <strong key={idx} style={{ color: peer ? '#E9EEF5' : 'var(--mcper-ink)' }}>{part}</strong> : <span key={idx}>{part}</span>))}
              </span>
            </div>
          )}
          {info && <small className="mcper-muted">{info.openRegistration ? i18n.publicHint(sso.map((p) => i18n.providerNamePublic[p] ?? i18n.providerName[p] ?? p)) : i18n.domainsHint(info.allowedEmailDomains)}</small>}
        </div>
      </Card>
      {peer && <span className="mcper-mono" style={{ position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center', fontSize: 12, color: '#7C8BA3' }}>{window.location.host}</span>}
    </div>
  );
}
